import { useCallback, useEffect, useMemo, useState, type Dispatch, type SetStateAction } from "react";
import { apiFetch, presignContractUpload, uploadToPresignedUrl } from "@/lib/api/client";
import { useAuth } from "@/contexts/AuthContext";
import type { ReviewFormData, ReviewWithOwner, ReviewReport } from "@/types";
import { useContractTypes, useCustomRolesDB } from "@/contexts/ReferenceDataContext";

export { ReferenceDataProvider, useContractTypes, useCustomRolesDB } from "@/contexts/ReferenceDataContext";

interface AsyncState<T> {
  isLoading: boolean;
  error: string | null;
  data: T;
}

function useAsyncState<T>(initialData: T): AsyncState<T> & {
  setData: Dispatch<SetStateAction<T>>;
  setError: (value: string | null) => void;
  setIsLoading: (value: boolean) => void;
} {
  const [data, setData] = useState<T>(initialData);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  return { data, error, isLoading, setData, setError, setIsLoading };
}

export function useReviews() {
  const { user } = useAuth();
  const { data, error, isLoading, setData, setError, setIsLoading } = useAsyncState<ReviewWithOwner[]>([]);

  const refresh = useCallback(async () => {
    if (!user) {
      setData([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);

    try {
      const allReviews = await apiFetch<ReviewWithOwner[]>("/api/reviews");
      setData(allReviews);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load reviews");
    } finally {
      setIsLoading(false);
    }
  }, [user, setData, setError, setIsLoading]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const deleteReview = useCallback(
    async (id: string, _contractFilePath: string | null) => {
      if (!user) throw new Error("Not signed in");
      await apiFetch(`/api/reviews/${id}`, { method: "DELETE" });
      setData((prev) => prev.filter((r) => r.id !== id));
    },
    [user, setData]
  );

  const getById = useCallback(
    async (id: string) => {
      if (!user) return null;
      const review = await apiFetch<ReviewWithOwner>(`/api/reviews/${id}`);
      return {
        ...review,
        is_owner: review.is_owner ?? review.user_id === user.id,
      } as ReviewWithOwner;
    },
    [user]
  );

  return {
    reviews: data,
    isLoading,
    error,
    refresh,
    deleteReview,
    getById,
  };
}

export function useSaveReview() {
  const { user } = useAuth();

  return useCallback(
    async (payload: {
      file: File | null;
      fileName: string;
      contractText: string | null;
      formData: ReviewFormData;
      result: ReviewReport;
    }) => {
      if (!user) return null;

      let contractFilePath: string | null = null;

      if (payload.file) {
        try {
          const { path, uploadUrl } = await presignContractUpload(payload.fileName);
          await uploadToPresignedUrl(uploadUrl, payload.file);
          contractFilePath = path;
        } catch (uploadErr) {
          console.error("Failed to upload file:", uploadErr);
        }
      }

      const data = await apiFetch<{ id: string }>("/api/reviews", {
        method: "POST",
        body: JSON.stringify({
          file_name: payload.fileName,
          contract_file_path: contractFilePath,
          contract_type: payload.formData.contractType,
          jurisdiction: payload.formData.jurisdiction,
          user_role: payload.formData.userRole,
          background_info: payload.formData.backgroundInfo,
          summary: payload.result.summary,
          overall_risk_score: payload.result.overallRiskScore,
          review_table: payload.result.reviewTable,
          recommendations: payload.result.recommendations,
          contract_text: payload.contractText,
          status: "completed",
        }),
      });

      return data?.id ?? null;
    },
    [user]
  );
}

export function useSelectOptions() {
  const { contractTypes } = useContractTypes();
  const { roles } = useCustomRolesDB();

  const contractTypeOptions = useMemo(
    () =>
      contractTypes.map((item) => ({
        id: item.id,
        value: item.value,
        label: item.name,
        guidelines: item.guidelines,
        isSystem: item.is_system,
      })),
    [contractTypes]
  );

  const roleOptions = useMemo(
    () =>
      roles.map((item) => ({
        id: item.id,
        value: item.role_key,
        label: item.name,
        instructions: item.instructions,
        isSystem: item.is_system,
      })),
    [roles]
  );

  return { contractTypeOptions, roleOptions };
}
